import { type ReactNode } from 'react'
import { type Id } from '@/infra/id'
import cx from 'classnames'
import { Dropdown, DropdownItem, DropdownTitle } from '@/components/Dropdown'
import styles from './BundleManagerCard.module.css'
import { IconButton } from '@/components/IconButton'
import { ArchivedItemsIndicator } from '@/features/admin/bundle/components/ArchivedItemsIndicator'
import { Checkbox } from '@/components/Checkbox'
import { Button } from '@/components/Button'

export interface BundleManagerWorkspaceCardProps {
  title: string
  isAllSelected: boolean
  isSomeSelected: boolean
  selectedCount: number
  onToggleAll: () => void
  onClearSelection: () => void
  showBulkActions: boolean
  onBulkApplyDiscount: () => void
  onBulkEditPrice: () => void
  onBulkDuplicate: () => void
  onBulkArchive: () => void
  /**
   * The bundle cards.
   */
  children?: ReactNode
  testId?: string
}

export function BundleManagerWorkspaceCard({
  title,
  isAllSelected,
  isSomeSelected,
  selectedCount,
  onToggleAll,
  onClearSelection,
  showBulkActions,
  onBulkApplyDiscount,
  onBulkEditPrice,
  onBulkDuplicate,
  onBulkArchive,
  children,
  testId,
}: BundleManagerWorkspaceCardProps) {
  return (
    <>
      <div className={styles.workspaceHeader}>
        <div>{title}</div>

        <div className={styles.actions}>
          <div className={styles.selection}>
            <Checkbox
              checked={isAllSelected}
              onChange={onToggleAll}
              testId="bundle-duplicate-select-all"
            />
            {isAllSelected ? 'All Selected' : 'Select All'}
          </div>

          {isSomeSelected && (
            <div className={styles.selection}>
              <span className={styles.separator}>|</span>
              <Checkbox checked="indeterminate" onChange={onClearSelection} />
              {selectedCount} Selected
            </div>
          )}

          {showBulkActions && (
            <Dropdown
              autoDismiss
              alignContent="right"
              position="bottom-end"
              minWidth={160}
              testId={testId && `${testId}-actions-button`}
              button={
                <Button
                  label="Actions"
                  type="submit"
                  className={styles.duplicateButton}
                  iconRight="dropdown"
                  iconRightFlush
                />
              }
            >
              <DropdownTitle>Actions</DropdownTitle>
              <DropdownItem
                onPress={onBulkApplyDiscount}
                testId={testId && `${testId}-apply-discount-action`}
              >
                Apply discount
              </DropdownItem>
              <DropdownItem
                onPress={onBulkEditPrice}
                testId={testId && `${testId}-edit-price-action`}
              >
                Edit price
              </DropdownItem>
              <DropdownItem
                onPress={onBulkDuplicate}
                testId={testId && `${testId}-duplicate-action`}
              >
                Duplicate
              </DropdownItem>
              <DropdownItem
                onPress={onBulkArchive}
                testId={testId && `${testId}-archive-action`}
              >
                Archive
              </DropdownItem>
            </Dropdown>
          )}
        </div>
      </div>

      <div className={styles.container}>{children}</div>
    </>
  )
}

export interface BundleManagerCardProps {
  id: Id
  name: string
  description: string
  price: number
  workspaceIconUrl: string
  includedItemsCount: number
  isArchived: boolean
  containsArchivedItems: boolean
  archivedItemsCount: number
  /**
   * `null` when the bundle has never been edited.
   */
  lastEditedByName: string | null
  createdByName: string
  isSelected: boolean
  onSelectedChange: (checked: boolean) => void
  onUnarchive: () => void
  onArchive: () => void
  onDuplicate: () => void
  onEdit: () => void
  testId?: string
}

export function BundleManagerCard({
  id,
  name,
  description,
  price,
  workspaceIconUrl,
  includedItemsCount,
  isArchived,
  containsArchivedItems,
  archivedItemsCount,
  lastEditedByName,
  createdByName,
  isSelected,
  onSelectedChange,
  onUnarchive,
  onArchive,
  onDuplicate,
  onEdit,
  testId,
}: BundleManagerCardProps) {
  const itemPlural = includedItemsCount === 1 ? ' item' : ' items'

  return (
    <div
      className={cx(
        styles.root,
        containsArchivedItems && styles.archivedItemsContainer,
      )}
      data-test={testId && `${testId}-card-${name}`}
    >
      <div className={styles.bundleHeader}>
        <div className={styles.workspaceIcon}>
          <img src={workspaceIconUrl || undefined} alt="" />
        </div>

        <div className={styles.bundleActions}>
          <Dropdown
            minWidth={120}
            testId={testId && `${testId}-actions-${name}`}
            alignContent="right"
            autoDismiss
            position="bottom-end"
            button={
              <IconButton
                name="more"
                label="Bundle actions"
                className={styles.dropdownIconButton}
                size={24}
                rotate={-90}
              />
            }
          >
            <DropdownTitle>Actions</DropdownTitle>
            {isArchived && (
              <DropdownItem
                onPress={onUnarchive}
                testId={testId && `${testId}-actions-unarchive-${name}`}
              >
                <div className={styles.rowActionBundle}>
                  <span>Unarchive</span>
                </div>
              </DropdownItem>
            )}
            {!isArchived && (
              <>
                <DropdownItem
                  onPress={onArchive}
                  testId={testId && `${testId}-actions-archive-${name}`}
                >
                  <div className={styles.rowActionBundle}>
                    <span>Archive</span>
                  </div>
                </DropdownItem>
                <DropdownItem
                  onPress={onDuplicate}
                  testId={testId && `${testId}-actions-duplicate-${name}`}
                >
                  <div className={styles.rowActionBundle}>
                    <span>Duplicate</span>
                  </div>
                </DropdownItem>
                <DropdownItem
                  onPress={onEdit}
                  testId={testId && `${testId}-actions-edit-${name}`}
                >
                  <div className={styles.rowActionBundle}>
                    <span>Edit</span>
                  </div>
                </DropdownItem>
              </>
            )}
          </Dropdown>

          <Checkbox
            checked={isSelected}
            onChange={onSelectedChange}
            testId={testId && `select-${testId}-${id}`}
          />
        </div>
      </div>

      <div className={styles.bundleName}>{name}</div>

      <div className={styles.bundleInfo}>
        <div className={styles.bundlePricing}>${price.toFixed(2)}</div>

        <div className={styles.separator}>•</div>

        <div className={styles.bundleIncludedItems}>
          {includedItemsCount + itemPlural}
        </div>
      </div>

      <div className={styles.bundleDescription}>{description}</div>

      <div className={styles.infoSeparator} />
      {!containsArchivedItems ? (
        <div className={styles.bundleLastUpdatedInfo}>
          {lastEditedByName !== null
            ? 'Last updated by ' + lastEditedByName
            : 'Created by ' + createdByName}
        </div>
      ) : (
        <ArchivedItemsIndicator count={archivedItemsCount} />
      )}
    </div>
  )
}
